// How old the information is, said out loud.
//
// Every page carries a `lastReviewed` date and every page rendered it as a bare string.
// A date alone does not tell most readers anything: "2026-06-02" reads as recent in
// June and is still sitting there, looking equally official, eighteen months later.
//
// Note on what these dates are NOT. All 51 states share one `lastReviewed` value,
// assigned once in the STATES mapper. That is accurate — one review pass covered the
// dataset — and giving each state its own date would mean inventing 51 review events
// that never happened, which is the exact thing this site refuses to do elsewhere. So
// the date stays shared and honest, and what improves is how plainly it is presented.

/** Past this, pages say how long it has been rather than only when it was. */
export const STALE_AFTER_DAYS = 180;

/**
 * Past this, something has gone wrong with maintenance rather than time simply
 * passing. Asserted in tests so an abandoned dataset fails a build instead of quietly
 * presenting itself as current.
 */
export const ABANDONED_AFTER_DAYS = 730;

const DAY_MS = 86_400_000;

/** Parse a YYYY-MM-DD review date at local midnight. Returns null if malformed. */
export function parseReviewDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function reviewAgeDays(iso: string, today: Date = new Date()): number | null {
  const reviewed = parseReviewDate(iso);
  if (!reviewed) return null;
  const a = Date.UTC(reviewed.getFullYear(), reviewed.getMonth(), reviewed.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.floor((b - a) / DAY_MS);
}

/** "3 months ago", "over a year ago" — never false precision like "97 days ago". */
export function describeAge(days: number): string {
  if (days < 0) return "in the future";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? "a month ago" : `${months} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? "over a year ago" : `over ${years} years ago`;
}

export interface Freshness {
  /** The date as published. */
  iso: string;
  ageDays: number | null;
  /** "6 months ago". Null when the date could not be parsed. */
  age: string | null;
  /** True once the age is worth stating rather than burying. */
  stale: boolean;
}

export function freshness(iso: string, today: Date = new Date()): Freshness {
  const ageDays = reviewAgeDays(iso, today);
  return {
    iso,
    ageDays,
    age: ageDays === null ? null : describeAge(ageDays),
    stale: ageDays !== null && ageDays >= STALE_AFTER_DAYS,
  };
}

/**
 * The line a page shows. Always names the date; adds the age once it is far enough back
 * that a reader would want to weigh it.
 */
export function reviewedLine(iso: string, label = "Reviewed", today: Date = new Date()): string {
  const f = freshness(iso, today);
  if (!f.age || f.ageDays === null) return `${label} ${iso}`;
  return f.stale ? `${label} ${iso} (${f.age})` : `${label} ${iso}`;
}
